import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import stockMessage from "../helpers/stock-message";
import getSellingPlanId from "../helpers/getSellingPlanId";
import CapacityIcon from "./capacityIcon";
import BlendedImage from "./blendedImage";
import { ArrowUpRightBox } from "../helpers/icons";
import SwatchImage from "./swatchImage";
import OptionValuePrice from "./optionValuePrice";
import morph from "../helpers/morph";
import { isColor, findColorOptionName } from "../helpers/colors";
import AliasedOption from "./aliased-option";

const shouldScrollToGallery = (option) =>
  isColor(option) || ["Car Seat Manufacturer"].includes(option);

const VariantPicker = ({ data, product, form, group }) => {
  const [currentProduct, setCurrentProduct] = useState(product);
  const hasAliases = currentProduct?.variants.edges.some(
    (edge) => edge.node.variantAliases,
  );
  const variants = currentProduct?.variants?.edges;
  const productGroup = group.products;

  const productGroupOptions = group.options;
  const groupStyle = currentProduct.tags
    ?.find((t) => t.includes("group:"))
    ?.split(":")[2];

  const colors = currentProduct.options.filter((o) => {
    return isColor(o.name);
  })[0];

  const options = currentProduct?.options?.filter(
    (option) =>
      !isColor(option.name) &&
      option.name != "Default Title" &&
      option.name != "Title",
  );

  const colorOptionName = findColorOptionName(currentProduct?.options);

  const initialVariant = variants?.find(({ node }) => {
    return node.id === data.initialVariantId;
  });

  // Set Initial Variant Options
  let variantOptions = currentProduct?.options.map((edge, optionIndex) => ({
    [edge?.name]:
      hasAliases && optionIndex === options.length - 1
        ? null
        : initialVariant?.node?.selectedOptions?.find(
            (element) => element.name == [edge?.name],
          ).value,
  }));

  const [variant, setVariant] = useState(initialVariant);
  const [groupUrl, setGroupurl] = useState(currentProduct.handle);
  const [selectedOptions, setSelectedOptions] = useState(variantOptions);

  useEffect(() => {
    const compatibleSizes =
      (variant?.node || variant)?.compatibleSizes?.reference ||
      product?.compatibleSizes?.reference ||
      null;

    const compatibleSizesData = {
      compatibleSizes: JSON.parse(compatibleSizes?.sizes?.value || "[]"),
      compatibleModels: JSON.parse(compatibleSizes?.models?.value || "[]"),
    };

    document.dispatchEvent(
      new CustomEvent("compatible-sizes", {
        detail: { compatibleSizesData },
      }),
    );
  }, [currentProduct, variant]);

  const handleColorVariants = () => {
    if (!colors) return;
    let so = Object.assign({}, ...selectedOptions);
    let vs = so
      ? variants.filter((v) => {
          let vo = currentProduct?.options.map((edge) => ({
            [edge?.name]: v?.node?.selectedOptions.find(
              (element) => element.name == [edge?.name],
            ).value,
          }));
          let voc = Object.assign({}, ...vo);
          return JSON.stringify(voc["Size"]) === JSON.stringify(so["Size"]);
        })
      : variants;
    return vs;
  };

  const [colorVariants, setColorVariants] = useState(handleColorVariants);

  useEffect(() => {
    setColorVariants(handleColorVariants());
  }, [selectedOptions, currentProduct]);

  const handleProductInfo = (sv, p) => {
    let cv = sv.node ? sv.node : sv;

    window.handleConfigPricing(cv, form);

    if (groupStyle && p) {
      setCurrentProduct(p);
      fetch(`/products/${p.handle}`)
        .then((response) => response.text())
        .then((data) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, "text/html");

          const mainContent = document.getElementById("MainContent");
          morph(mainContent, doc.getElementById("MainContent"));
        });
    }
  };

  const handleVariantChange = (option, val) => {
    let sv = null,
      svo = null,
      sp = null;
    if (val.node) {
      sp = val.node;
      sv = val.node.variants.edges[0].node;
      svo = sv.selectedOptions.map((option) => ({
        [option.name]: option.value,
      }));
      setGroupurl(val.node.handle);
    } else {
      const selectedOption = selectedOptions.map((o) => {
        if (Object.keys(o) == option) {
          return {
            ...o,
            [option]: val,
          };
        }
        return o;
      });
      const currentOption = {
        [option]: val,
      };

      let vset =
        productGroup && option.toLowerCase() == groupStyle
          ? productGroup
          : variants;

      let selectedVariant = vset.filter((v) => {
        let vo = currentProduct?.options.map((edge) => ({
          [edge?.name]: v?.node?.selectedOptions.find(
            (element) => element.name == [edge?.name],
          ).value,
        }));
        return JSON.stringify(vo) === JSON.stringify(selectedOption);
      });

      if (!selectedVariant[0]?.node) {
        selectedVariant = vset.filter((v) => {
          let set = v?.node?.selectedOptions.find(
            (element) => element.name == [option] && element.value == [val],
          );
          return set?.value === currentOption[option];
        });
      }

      sv = selectedVariant.length > 0 ? selectedVariant[0] : selectedVariant;
      svo = sv.node.selectedOptions.map((option) => ({
        [option.name]: option.value,
      }));
      sv = sv.node ? sv.node : sv;

      if (shouldScrollToGallery(option)) {
        const promoBar = document.querySelector("section-promo-bar");

        if (!(promoBar && window.scrollY < promoBar.scrollHeight)) {
          const offset = promoBar ? promoBar.scrollHeight : 0;
          window.scroll({ top: 0 + offset, left: 0, behavior: "smooth" });
        }
      }
    }
    setSelectedOptions(svo);
    setVariant(sv);

    if (!groupStyle) {
      form.querySelectorAll("[data-product-price]").forEach((el) => {
        el.innerHTML = `
        <span class="price__regular mr-0">${window.formatMoney(
          sv.priceV2.amount,
        )}</span>
        ${
          sv.compareAtPriceV2 &&
          parseFloat(sv.compareAtPriceV2.amount) > parseFloat(sv.priceV2.amount)
            ? `<del class="price__compare">${window.formatMoney(
                sv.compareAtPriceV2.amount,
              )}</del>`
            : ""
        }
        `;
      });
    }

    form.querySelectorAll("[data-add-message]").forEach((message) => {
      message.closest("button").disabled = false;

      if (!sv.availableForSale) {
        message.innerHTML = "Out of Stock";
        message.closest("button").disabled = true;
        return;
      }

      message.innerHTML =
        sv.preorder?.value === "true" ? "Pre-Order" : "Add to Cart";
    });

    if (variant === sv) return;
    let newCurrentProduct = sp ? sp : null;

    form.dispatchEvent(
      new CustomEvent("product-form:update", {
        detail: {
          product: newCurrentProduct,
          currentProduct: newCurrentProduct,
        },
      }),
    );

    const productUpdated = new CustomEvent("productUpdate", {
      detail: {
        ...(newCurrentProduct && { product: newCurrentProduct }),
        variant: sv,
      },
    });

    handleProductInfo(sv, newCurrentProduct);
    document.dispatchEvent(productUpdated);
    updateProperties(sv, val?.node?.handle);
    window.handleConfigPricing(sv, form);
  };

  const updateProperties = (sv, gu) => {
    if (!sv) return;

    let v = sv.node ? sv.node : sv,
      id = v.id.split("/").pop();
    const newUrl = new URL(window.location);
    newUrl.pathname = gu
      ? newUrl.pathname.replace(/\/[^\/]*$/, `/${gu}`)
      : newUrl.pathname;
    newUrl.searchParams.set("variant", id);
    window.history.replaceState({}, "", newUrl);
    const input = form?.querySelector('input[name="id"]');
    input.value = id;
    form.dispatchEvent(
      new CustomEvent("product-form:update", {
        detail: {
          currentVariant: v,
        },
      }),
    );
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const handleGroupActive = (name, product) => {
    return product.node.handle == groupUrl;
  };

  const handleVariantActive = (name, val) => {
    let vo = Object.assign({}, ...selectedOptions);
    return vo[name] == val ? "--active" : "";
  };

  const findVariant = (name, value) => {
    const variant = variants
      // Find variants for selected 1st tier
      .filter(({ node }) => {
        const optionsWithMultipleValues = currentProduct.options.filter(
          (option) => option.optionValues.length > 1,
        );

        if (optionsWithMultipleValues.length === 1) return true;

        if (
          node.selectedOptions[0].value ===
          selectedOptions[0][node.selectedOptions[0].name]
        )
          return true;

        return false;
      })
      // Find variant for this option value
      .find(({ node }) =>
        node.selectedOptions.some(
          (option) => option.name === name && option.value === value,
        ),
      );

    if (!variant) return null;

    return variant.node;
  };

  const firstButHasOtherOptions = (name) => {
    const optionsWithMultipleValues = currentProduct.options.filter(
      (option) => option.optionValues.length > 1,
    );

    return (
      name === currentProduct.options[0].name &&
      optionsWithMultipleValues.length > 1
    );
  };

  const findStockMessageFor = (name, value) => {
    // Don't display stock for 1st tier chips unless that is the only tier
    if (firstButHasOtherOptions(name)) {
      return null;
    }

    const variant = findVariant(name, value);

    if (!variant) return null;

    return stockMessage(variant);
  };

  const findVariantFor = (name, value) => {
    // Don't display price for 1st tier chips unless that is the only tier
    if (firstButHasOtherOptions(name)) {
      return null;
    }

    return findVariant(name, value);
  };

  useEffect(() => {
    setTimeout(() => {
      // Preselect variant matching the requested color.
      const { searchParams } = new URL(window.location);
      const param = searchParams.get("color");

      if (!param) return;

      const value = decodeURIComponent(param);

      const matchingOption = currentProduct.options.find(
        (option) => option.name === colorOptionName,
      );

      if (!matchingOption || !matchingOption.values.includes(value)) return;

      handleVariantChange(colorOptionName, value);
    }, 500);
  }, [product]);

  return (
    <Fragment>
      <input
        type="hidden"
        name="selling_plan"
        value={getSellingPlanId(variant.node || variant)}
      />
      <div className="product-form__variants h5">
        {options?.map((option, index) =>
          option.name.toLowerCase() == groupStyle && productGroupOptions ? (
            <Fragment>
              <div class="product-form__variants__heading__wrapper">
                <div className="product-form__variants__heading h5">
                  Select {groupStyle}
                  <a
                    href="#"
                    class="button-text product-form__help-me-choose-cta"
                    data-modal-trigger="compare-modal"
                  >
                    <span>Help Me Choose</span>
                    <ArrowUpRightBox />
                  </a>
                </div>
              </div>
              <span class="hidden"></span>
              <div
                className={`product-form__options product-form__options--single-column ${groupStyle}`}
              >
                {productGroup?.map((item, i) => (
                  <div className="product-form__option">
                    <input
                      type="radio"
                      id={`template--${option?.name}-${i}`}
                      name={option?.name}
                      value={item.node.title}
                      form={`form-${option.name}`}
                      checked={handleGroupActive(option?.name, item)}
                    />

                    <label
                      for={`template--${option?.name}-${i}`}
                      aria-label={option?.name}
                      className={`text product-form__option-label ${item.node.variants.edges[0].node.image?.src ? "" : "product-form__option-label--no-image"}`}
                      onClick={() => {
                        handleVariantChange(option?.name, item);
                      }}
                    >
                      <BlendedImage
                        className="product-form__option-variant-image"
                        src={item.node.variants.edges[0].node.image?.src}
                        size="200x"
                      />
                      <div className="product-form__option-text">
                        <span>{item.node.title}</span>

                        {groupStyle != "Size" && (
                          <span
                            class="product-form__option-availability"
                            data-stock
                          >
                            {stockMessage(item.node.variants.edges[0].node)}
                          </span>
                        )}
                        <CapacityIcon
                          className="product-form__option-capacity-icon"
                          node={item.node.variants.edges[0].node}
                        />
                      </div>
                      <OptionValuePrice
                        variant={item.node.variants.edges[0].node}
                        selectedVariant={variant?.node ? variant.node : variant}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </Fragment>
          ) : (
            option.values.length > 1 && (
              <Fragment>
                <div className="product-form__variants__heading h5">
                  Select {option.name}
                </div>
                <span class="hidden"></span>
                <div
                  className={`product-form__options ${option?.name} ${currentProduct.options.length <= 1 || (hasAliases && index === currentProduct.options.length - 1) ? "product-form__options--single-column" : ""}`}
                >
                  {hasAliases && index === currentProduct.options.length - 1 ? (
                    <AliasedOption
                      product={currentProduct}
                      option={option}
                      selectedOptions={selectedOptions}
                      handleVariantChange={handleVariantChange}
                      active={true}
                    ></AliasedOption>
                  ) : (
                    option.values?.map((item, i) =>
                      index === 0 || findVariantFor(option.name, item) ? (
                        <Fragment>
                          <div className="product-form__option">
                            <input
                              type="radio"
                              id={`template--${option?.name}-${i}`}
                              name={option?.name}
                              value={item}
                              form={`form-${option.name}`}
                              checked={handleVariantActive(option?.name, item)}
                            />

                            <label
                              for={`template--${option?.name}-${i}`}
                              aria-label="Cruiser 2-Seater"
                              className={`text product-form__option-label ${findVariantFor(option.name, item)?.image?.src ? "" : "product-form__option-label--no-image"}`}
                              onClick={() => {
                                handleVariantChange(option?.name, item);
                              }}
                            >
                              {currentProduct.options.length <= 1 && (
                                <BlendedImage
                                  className="product-form__option-variant-image"
                                  src={
                                    findVariantFor(option.name, item)?.image
                                      ?.src
                                  }
                                  size="200x"
                                />
                              )}
                              <div className="product-form__option-text">
                                <span>{item}</span>
                                <span
                                  class="product-form__option-availability"
                                  data-stock
                                >
                                  {findStockMessageFor(option.name, item)}
                                </span>
                                {currentProduct.options.length <= 1 && (
                                  <CapacityIcon
                                    className="product-form__option-capacity-icon"
                                    node={findVariantFor(option.name, item)}
                                  />
                                )}
                              </div>
                              <OptionValuePrice
                                variant={findVariantFor(option.name, item)}
                                selectedVariant={
                                  variant?.node ? variant.node : variant
                                }
                              />
                            </label>
                          </div>
                        </Fragment>
                      ) : null,
                    )
                  )}
                </div>
              </Fragment>
            )
          ),
        )}
        {colorVariants?.length > 1 && (
          <Fragment>
            <div className="product-form__variants__heading h5">
              Select {colors.name}
            </div>
            <span class="hidden"></span>
            <div className={`product-form__options ${colors?.name}`}>
              {colorVariants?.map((item, i) => (
                <Fragment>
                  <div className="product-form__option">
                    <input
                      type="radio"
                      id={`template--${colors?.name}-${i}`}
                      name={colors?.name}
                      value={item.node.title.split(" / ").pop()}
                      form={`form-${colors.name}`}
                      checked={handleVariantActive(
                        colorOptionName,
                        item.node.title.split(" / ").pop(),
                      )}
                    />

                    <label
                      for={`template--${colors?.name}-${i}`}
                      aria-label="Cruiser 2-Seater"
                      className="text product-form__option-label--color"
                      onClick={() => {
                        handleVariantChange(
                          colorOptionName,
                          item.node.title.split(" / ").pop(),
                        );
                      }}
                    >
                      <SwatchImage
                        product={currentProduct}
                        handle={currentProduct?.handle}
                        optionValue={item?.node?.title.split(" / ").pop()}
                        className="product-form__option-swatch"
                      />
                      <div className="product-form__option-text">
                        <span>{item.node.title.split(" / ").pop()}</span>
                        <span
                          class="product-form__option-availability"
                          data-stock
                        >
                          {stockMessage(item.node)}
                        </span>
                      </div>
                      <OptionValuePrice
                        variant={item.node}
                        selectedVariant={variant?.node ? variant.node : variant}
                      />
                    </label>
                  </div>
                </Fragment>
              ))}
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default VariantPicker;
